import {Box, Tooltip, Typography} from '@mui/material';
import dayjs from 'dayjs';
import {useMemo} from 'react';
import {useSelector} from 'react-redux';

import {TimeOfDayCountRow} from '../../../interfaces/HazardAITimeOfDay';
import reduxSelectors from '../../../redux/selectors';

interface Props {
  rows: TimeOfDayCountRow[];
  dateStart: string;
  dateEnd: string;
  onCellClick: (date: string, hour: number) => void;
}

const HOURS = Array.from({length: 24}, (_, i) => i);

/** Interpolate green → yellow → orange → red based on 0..1 ratio */
const intensityColor = (ratio: number): string => {
  if (ratio <= 0) {
    return 'transparent';
  }
  // Clamp
  const r = Math.max(0, Math.min(1, ratio));
  // green(0,200,0) → yellow(255,200,0) → orange(255,100,0) → red(200,0,0)
  let red: number, green: number, blue: number;
  if (r < 0.33) {
    const t = r / 0.33;
    red = Math.round(t * 255);
    green = 200;
    blue = 0;
  } else if (r < 0.66) {
    const t = (r - 0.33) / 0.33;
    red = 255;
    green = Math.round(200 - t * 100);
    blue = 0;
  } else {
    const t = (r - 0.66) / 0.34;
    red = Math.round(255 - t * 55);
    green = Math.round(100 - t * 100);
    blue = 0;
  }
  return `rgb(${red},${green},${blue})`;
};

/** Pick legible text color given background intensity ratio */
const textColor = (ratio: number, isDark: boolean): string => {
  if (ratio <= 0) {
    return isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)';
  }
  return ratio > 0.45 ? '#fff' : isDark ? '#ddd' : '#111';
};

export const TimeOfDayGrid = ({rows, dateStart, dateEnd, onCellClick}: Props) => {
  const isDarkMode = useSelector(reduxSelectors.app.getIsDarkMode);

  // Build a lookup: date -> hour -> total
  const matrix = useMemo<Record<string, Record<number, number>>>(() => {
    const m: Record<string, Record<number, number>> = {};
    rows.forEach((r) => {
      if (!m[r.date]) {
 m[r.date] = {};
}
      m[r.date][r.hour] = r.total;
    });
    return m;
  }, [rows]);

  // Enumerate all dates in range
  const dates = useMemo<string[]>(() => {
    const result: string[] = [];
    if (!dateStart || !dateEnd) {
 return result;
}
    let cur = dayjs(dateStart);
    const end = dayjs(dateEnd);
    while (!cur.isAfter(end)) {
      result.push(cur.format('YYYY-MM-DD'));
      cur = cur.add(1, 'day');
    }
    return result;
  }, [dateStart, dateEnd]);

  // Per-day totals (TOTAL column)
  const dayTotals = useMemo<Record<string, number>>(() => {
    const t: Record<string, number> = {};
    dates.forEach((d) => {
      t[d] = HOURS.reduce((s, h) => s + (matrix[d]?.[h] ?? 0), 0);
    });
    return t;
  }, [dates, matrix]);

  // Per-hour totals (TOTALS row)
  const hourTotals = useMemo<Record<number, number>>(() => {
    const t: Record<number, number> = {};
    HOURS.forEach((h) => {
      t[h] = dates.reduce((s, d) => s + (matrix[d]?.[h] ?? 0), 0);
    });
    return t;
  }, [dates, matrix]);

  // Grand total
  const grandTotal = useMemo(
    () => Object.values(dayTotals).reduce((s, n) => s + n, 0),
    [dayTotals]
  );

  // Max cell value across all cells (auto-scale)
  const maxCount = useMemo(() => {
    let m = 1;
    dates.forEach((d) => {
      HOURS.forEach((h) => {
        const v = matrix[d]?.[h] ?? 0;
        if (v > m) {
 m = v;
}
      });
    });
    return m;
  }, [dates, matrix]);

  const cellSx = {
    width: 36,
    minWidth: 36,
    height: 26,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.7rem',
    fontWeight: 500,
    border: '1px solid',
    borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    cursor: 'default',
    flexShrink: 0,
    userSelect: 'none' as const,
  };

  const headerCellSx = {
    ...cellSx,
    bgcolor: isDarkMode ? 'grey.800' : 'grey.200',
    color: isDarkMode ? 'grey.300' : 'grey.700',
    fontWeight: 700,
    cursor: 'default',
  };

  const totalCellSx = {
    ...cellSx,
    bgcolor: isDarkMode ? 'grey.700' : 'grey.300',
    color: isDarkMode ? 'grey.200' : 'grey.800',
    fontWeight: 700,
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      height="100%"
      overflow="auto"
      sx={{
        '&::-webkit-scrollbar': {height: 6, width: 6},
        '&::-webkit-scrollbar-thumb': {
          bgcolor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
          borderRadius: 3,
        },
      }}
    >
      {/* Sticky header row */}
      <Box
        display="flex"
        position="sticky"
        top={0}
        zIndex={2}
        bgcolor={isDarkMode ? 'background.paper' : 'background.paper'}
      >
        {/* Date label column */}
        <Box sx={{...headerCellSx, width: 90, minWidth: 90}}>Date</Box>
        {/* Hour headers */}
        {HOURS.map((h) => (
          <Box key={h} sx={headerCellSx}>
            {String(h).padStart(2, '0')}
          </Box>
        ))}
        {/* TOTAL header */}
        <Box sx={{...headerCellSx, width: 60, minWidth: 60}}>Total</Box>
      </Box>

      {/* Date rows */}
      {dates.map((date) => (
        <Box key={date} display="flex" flexShrink={0}>
          {/* Date label */}
          <Box
            sx={{
              ...headerCellSx,
              width: 90,
              minWidth: 90,
              justifyContent: 'flex-end',
              pr: 0.5,
              position: 'sticky',
              left: 0,
              zIndex: 1,
            }}
          >
            {dayjs(date).format('D MMM')}
          </Box>

          {/* Hour cells */}
          {HOURS.map((h) => {
            const count = matrix[date]?.[h] ?? 0;
            const ratio = count / maxCount;
            const bg = intensityColor(ratio);
            const fg = textColor(ratio, isDarkMode);
            const clickable = count > 0;

            return (
              <Tooltip
                key={h}
                title={
                  count > 0
                    ? `${date} ${String(h).padStart(2, '0')}:00 — ${count} detection${count !== 1 ? 's' : ''}`
                    : ''
                }
                disableHoverListener={count === 0}
                placement="top"
                arrow
              >
                <Box
                  sx={{
                    ...cellSx,
                    bgcolor: bg,
                    color: fg,
                    cursor: clickable ? 'pointer' : 'default',
                    '&:hover': clickable
                      ? {outline: '2px solid', outlineColor: 'primary.main', zIndex: 1}
                      : {},
                  }}
                  onClick={() => clickable && onCellClick(date, h)}
                >
                  {count > 0 ? count : ''}
                </Box>
              </Tooltip>
            );
          })}

          {/* Day total */}
          <Box sx={{...totalCellSx, width: 60, minWidth: 60}}>
            {dayTotals[date] || ''}
          </Box>
        </Box>
      ))}

      {/* TOTALS row */}
      <Box
        display="flex"
        flexShrink={0}
        position="sticky"
        bottom={24}
        bgcolor={isDarkMode ? 'background.paper' : 'background.paper'}
        zIndex={2}
      >
        <Box sx={{...headerCellSx, width: 90, minWidth: 90, justifyContent: 'flex-end', pr: 0.5}}>
          Totals
        </Box>
        {HOURS.map((h) => (
          <Box key={h} sx={totalCellSx}>
            {hourTotals[h] || ''}
          </Box>
        ))}
        {/* Grand total */}
        <Box sx={{...totalCellSx, width: 60, minWidth: 60, fontWeight: 900}}>
          {grandTotal || ''}
        </Box>
      </Box>

      {/* Legend */}
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        py={0.5}
        flexShrink={0}
        position="sticky"
        bottom={0}
        bgcolor={isDarkMode ? 'background.paper' : 'background.paper'}
        borderTop="1px solid"
        borderColor="divider"
      >
        {/* Spacer matching the sticky date label column so the scale starts under hour 00 */}
        <Box sx={{width: 90, minWidth: 90, flexShrink: 0}} />
        <Typography variant="caption" color="text.secondary">
          0
        </Typography>
        <Box
          sx={{
            height: 10,
            width: 180,
            background: `linear-gradient(to right, ${intensityColor(0.05)}, ${intensityColor(0.33)}, ${intensityColor(0.66)}, ${intensityColor(1)})`,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {maxCount}/hr
        </Typography>
        <Typography variant="caption" color="text.secondary" ml={1}>
          (auto-scale)
        </Typography>
      </Box>
    </Box>
  );
};
